// TwoFAVerify.jsx
import React from "react";

export const theme = {
  bg: "#131313",
  panel: "#030504",
  text: "#e6eaf2",
  muted: "#b0b0b0",
  primary: "#4bb390",
  accent: "#22d3ee",
  danger: "#ef4444",
  radius: 16,
  border: "1px solid var(--border-color)",
};

const wrap = (s) => Object.assign({ boxSizing: "border-box" }, s);

export default function TwoFAVerify({ onBack, onSubmit, loading = false, error = null }) {
  const [code, setCode] = React.useState("");

  const normalize = (v) => v.replace(/\D/g, "").slice(0, 6);

  const handleChange = (e) => {
    setCode(normalize(e.target.value));
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    const next = normalize(text);
    if (next) {
      e.preventDefault();
      setCode(next);
    }
  };

  const canSubmit = code.length === 6 && !loading;

  const submit = (e) => {
    e.preventDefault();
    if (canSubmit && onSubmit) onSubmit(code);
  };

  return (
    <div
      style={wrap({
        background: theme.bg,
        color: theme.text,
        display: "grid",
        placeItems: "center",
        padding: 24,
        fontFamily: "Inter, system-ui, sans-serif",
        border:theme.border,
        borderRadius: 24,
      })}
    >
      <section
        style={wrap({
          width: "min(640px, 100%)",
          background: theme.panel,
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,.06)",
          padding: 28,
          boxShadow: "0 18px 60px rgba(0,0,0,.55)",
        })}
      >
        <h1 style={wrap({ margin: "0 0 6px", fontSize: 26 })}>
          Two‑Factor Authentication Setup
        </h1>
        <p style={wrap({ color: theme.muted, margin: "0 0 18px" })}>
          Enter the 6‑digit code from the authenticator app to complete setup.
        </p>

        {error && (
          <div
            style={wrap({
              background: "rgba(239,68,68,.12)",
              color: "#fecaca",
              border: "1px solid rgba(239,68,68,.35)",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 18,
            })}
          >
            {error}
          </div>
        )}

        <label
          htmlFor="otp"
          style={wrap({ display: "block", textAlign: "center", marginBottom: 8 })}
        >
          <span style={wrap({ fontWeight: 600 })}>Enter 6‑digit code</span>
        </label>

        <form onSubmit={submit}>
          <input
            id="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={handleChange}
            onPaste={handlePaste}
            placeholder="000000"
            style={wrap({
              width: "100%",
              height: 56,
              borderRadius: 12,
              background: "#0b0d10",
              border: "1px solid rgba(255,255,255,.08)",
              color: "#cfe0ff",
              textAlign: "center",
              fontSize: 22,
              letterSpacing: 6,
              outline: "none",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)",
            })}
          />

          <div
            style={wrap({
              display: "flex",
              gap: 14,
              justifyContent: "center",
              marginTop: 18,
              flexWrap: "wrap",
            })}
          >
            <button
              type="button"
              onClick={onBack}
              style={wrap({
                background: "#111317",
                color: theme.text,
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 12,
                padding: "12px 18px",
                minWidth: 140,
                cursor: "pointer",
              })}
            >
              Back
            </button>

            <button
              type="submit"
              disabled={!canSubmit}
              style={wrap({
                background: canSubmit ? theme.primary : "#0e2a22",
                color: canSubmit ? "#06110d" : "#6a7a75",
                border: "1px solid rgba(0,0,0,.25)",
                borderRadius: 12,
                padding: "12px 18px",
                minWidth: 160,
                fontWeight: 800,
                cursor: canSubmit ? "pointer" : "not-allowed",
                opacity: canSubmit ? 1 : 0.6,
              })}
            >
              {loading ? "Verifying..." : "Enable 2FA"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
